import { useState, useEffect, useCallback } from 'react';

import api from 'api';

interface SidebarClient {
  id: number | string;
  name: string;
  [key: string]: unknown;
}

interface UseSidebarClientsOptions {
  skip?: boolean;
}

interface UseSidebarClientsReturn {
  data: SidebarClient[];
  loading: boolean;
  error: unknown;
  reload: () => void;
}

export const useSidebarClients = ({
  skip = false,
}: UseSidebarClientsOptions = {}): UseSidebarClientsReturn => {
  const [clients, setClients] = useState<SidebarClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [loaded, setLoaded] = useState(false);

  const loadClients = useCallback(async () => {
    if (skip || loading || loaded) return;

    setLoading(true);
    setError(null);

    try {
      const { data } = await api.getCollection('clients', {
        pageSize: 1000,
        pageIndex: 0,
        sortBy: [{ id: 'name', desc: false }],
        filters: {},
        searchTerms: '',
        forceSearch: false,
      });

      setClients((data as SidebarClient[]) || []);
      setLoaded(true);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [skip, loading, loaded]);

  useEffect(() => {
    if (!skip) {
      loadClients();
    }
  }, [skip]);

  const reload = useCallback(() => {
    setLoaded(false);
    loadClients();
  }, [loadClients]);

  return {
    data: clients,
    loading,
    error,
    reload,
  };
};
